import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@mui/material";
import {
  Business,
  CheckCircle,
  Cancel,
  Visibility,
  Edit,
  Warning,
  Refresh,
  Search,
  FilterList
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../config/api";
import useSEO from "../hooks/useSEO";

const AdminPanel = () => {
  useSEO({ title: 'Админ панель | JobMD' });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [filter, setFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, page: 1, limit: 20 });

  // Проверяем, является ли пользователь администратором
  const isAdmin = () => {
    const userId = localStorage.getItem("userId");
    return userId === "1";
  };

  useEffect(() => {
    console.log("🐛 AdminPanel: useEffect запущен");
    console.log("🐛 Проверка isAdmin():", isAdmin());
    console.log("🐛 localStorage userId:", localStorage.getItem("userId"));
    
    if (!isAdmin()) {
      setError("Доступ запрещен. Требуются права администратора.");
      setLoading(false);
      return;
    }
    loadJobs();
  }, [filter, page]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("userId");
      console.log("🐛 Загрузка вакансий... userId:", userId, "filter:", filter, "page:", page);
      
      const response = await fetch(`${apiUrl("employer_jobs.php")}?action=admin-list&userId=${userId}&filter=${filter}&page=${page}&limit=20`);
      console.log("🐛 API Response status:", response.status);
      const data = await response.json();
      console.log("🐛 API Response data:", data);
      
      if (data.success) {
        console.log("🐛 Вакансий загружено:", data.jobs?.length);
        setJobs(data.jobs || []);
        setStats(data.stats || {});
        if (data.pagination) {
          setPagination(data.pagination);
        }
      } else {
        console.log("🐛 API ошибка:", data.message);
        setError(data.message);
      }
    } catch (err) {
      console.log("🐛 Ошибка загрузки:", err);
      setError("Ошибка загрузки данных: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (jobId, status) => {
    try {
      const userId = localStorage.getItem("userId");
      const url = status === 'published' 
        ? `${apiUrl("employer_jobs.php")}?action=publish&userId=${userId}&jobId=${jobId}`
        : `${apiUrl("employer_jobs.php")}?action=reject&userId=${userId}&jobId=${jobId}`;
      
      const method = 'POST';
      const body = status === 'rejected' 
        ? JSON.stringify({ reject_reason: rejectReason })
        : null;
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body
      });

      const data = await response.json();
      if (data.success) {
        setVerifyDialog(false);
        setRejectDialog(false);
        setRejectReason("");
        setSuccess(`Вакансия ${status === 'published' ? 'опубликована' : 'отклонена'}!`);
        loadJobs();
      } else {
        setError("Ошибка: " + data.message);
      }
    } catch (err) {
      setError("Ошибка: " + err.message);
    }
  };

  const handleViewJob = (jobId) => {
    navigate(`/employer-job/${jobId}`);
  };

  const handleViewCompany = (employerId) => {
    // Можно добавить страницу просмотра компании
    console.log("Просмотр компании ID:", employerId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'success';
      case 'pending_review': return 'warning';
      case 'rejected': return 'error';
      case 'draft': return 'default';
      default: return 'info';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'published': return 'Опубликована';
      case 'pending_review': return 'На проверке';
      case 'rejected': return 'Отклонена';
      case 'draft': return 'Черновик';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("ru-RU");
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (!isAdmin()) {
    return (
      <Box sx={{ maxWidth: "1200px", mx: "auto", p: 4 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          Доступ запрещен. Требуются права администратора.
        </Alert>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate("/")}>
          На главную
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const tabFilters = [
    { label: "На проверке", value: "pending", count: stats.pending || 0 },
    { label: "Опубликованные", value: "published", count: stats.published || 0 },
    { label: "Отклоненные", value: "rejected", count: stats.rejected || 0 },
    { label: "Черновики", value: "draft", count: stats.draft || 0 }
  ];

  return (
    <Box sx={{ maxWidth: "1400px", mx: "auto", p: { xs: 2, md: 3 } }}>
      {/* Уведомления */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Заголовок */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Админ-панель 🛡️
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Модерация вакансий от работодателей
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadJobs}>
            Обновить
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
            Всего: {stats.total || 0}
          </Typography>
        </Stack>
      </Box>

      {/* Статистика */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="warning.main">
                {stats.pending || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                На проверке
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="success.main">
                {stats.published || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Опубликовано
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="error.main">
                {stats.rejected || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Отклонено
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4">
                {stats.draft || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Черновики
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Вкладки фильтров */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => {
          setActiveTab(newValue);
          setFilter(tabFilters[newValue].value);
          setPage(1);
        }}>
          {tabFilters.map((tab, index) => (
            <Tab 
              key={tab.value}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {tab.label}
                  {tab.count > 0 && (
                    <Chip label={tab.count} size="small" color={
                      tab.value === 'pending' ? 'warning' :
                      tab.value === 'published' ? 'success' :
                      tab.value === 'rejected' ? 'error' : 'default'
                    } />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Список вакансий */}
      {jobs.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Business sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Нет вакансий для модерации
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filter === 'pending' 
                ? "Все вакансии проверены!" 
                : `Нет вакансий со статусом "${getStatusText(filter)}"`}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Вакансия</TableCell>
                <TableCell>Компания</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Дата создания</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <Typography fontWeight="medium">{job.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {truncateText(job.description, 80)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {job.location_city} • {job.employment_type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="medium">{job.company_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {job.employer_email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusText(job.verification_status)} 
                      color={getStatusColor(job.verification_status)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>{formatDate(job.created_at)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton 
                        size="small" 
                        color="info" 
                        onClick={() => handleViewJob(job.id)}
                        title="Просмотреть вакансию"
                      >
                        <Visibility />
                      </IconButton>
                      
                      {job.verification_status === 'pending_review' && (
                        <>
                          <IconButton 
                            size="small" 
                            color="success" 
                            onClick={() => {
                              setSelectedJob(job);
                              setVerifyDialog(true);
                            }}
                            title="Опубликовать"
                          >
                            <CheckCircle />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => {
                              setSelectedJob(job);
                              setRejectDialog(true);
                            }}
                            title="Отклонить"
                          >
                            <Cancel />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Пагинация */}
      {pagination.pages > 1 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} mb={2}>
          <Typography variant="body2" color="text.secondary">
            Всего: {pagination.total} вакансий — стр. {pagination.page} из {pagination.pages}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              ← Назад
            </Button>
            {(() => {
              const p = pagination.page;
              const total = pagination.pages;
              const start = Math.max(1, p - 2);
              const end = Math.min(total, p + 2);
              const pages = [];
              for (let i = start; i <= end; i++) {
                pages.push(i);
              }
              return pages.map(i => (
                <Button
                  key={i}
                  variant={i === p ? "contained" : "outlined"}
                  size="small"
                  sx={{ minWidth: 36 }}
                  onClick={() => setPage(i)}
                >
                  {i}
                </Button>
              ));
            })()}
            <Button
              variant="outlined"
              size="small"
              disabled={page >= pagination.pages}
              onClick={() => setPage(p => p + 1)}
            >
              Вперед →
            </Button>
          </Stack>
        </Box>
      )}

      {/* Диалог публикации */}
      <Dialog open={verifyDialog} onClose={() => setVerifyDialog(false)}>
        <DialogTitle>Опубликовать вакансию</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Вы уверены, что хотите опубликовать вакансию?
          </Typography>
          {selectedJob && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2">{selectedJob.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedJob.company_name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog(false)}>Отмена</Button>
          <Button 
            onClick={() => handleVerify(selectedJob?.id, 'published')} 
            variant="contained" 
            color="success"
          >
            Опубликовать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог отклонения */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Отклонить вакансию</DialogTitle>
        <DialogContent>
          <Typography gutterBottom sx={{ mb: 2 }}>
            Укажите причину отклонения вакансии:
          </Typography>
          {selectedJob && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2">{selectedJob.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedJob.company_name}
              </Typography>
            </Box>
          )}
          <TextField
            label="Причина отклонения"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Например: несоответствие требованиям, некорректная информация и т.д."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Отмена</Button>
          <Button 
            onClick={() => handleVerify(selectedJob?.id, 'rejected')} 
            variant="contained" 
            color="error"
            disabled={!rejectReason.trim()}
          >
            Отклонить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPanel;